import React, { useContext, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { ListType } from '@/components/pages/setting/guest-room/key-number'
import { Control, Controller, useFieldArray } from 'react-hook-form'
import { TableHeader, TableBody, TableRow, TableDisabledOverlay } from '@/components/atoms/settings/table'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { RemoteLockClient, RemoteLockContextCreator } from '@/contexts/remote-lock'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { getSmartLockDevices } from '@/apis/aipass'
import { Device, LockType } from '@/models/lock-setting'

type Props = {
  control: Control<{ assign: ListType[] }, any>
  onChange: () => void
}

export const SettingRemoteLockGuestRoomKeyList: React.FC<Props> = ({ control, onChange: parentOnChange }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const remoteLockClient = useContext<RemoteLockClient>(RemoteLockContextCreator())

  const [remoteLockDeviceList, setRemoteLockDeviceList] = useState<Device[]>()

  const assignForm = useFieldArray({
    control,
    name: 'assign',
  })

  const fetchDevice = async () => {
    if (!remoteLockClient.isConnected) {
      return
    }
    try {
      setIsLoading(true)
      await getSmartLockDevices({ lockType: LockType.RemoteLock }).then(setRemoteLockDeviceList)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const deviceIdAndSerial = useMemo(() => {
    const result: { [key: string]: string } = {}
    remoteLockDeviceList?.forEach(device => {
      result[device.id] = device.serialNumber || ''
    })
    return result
  }, [remoteLockDeviceList])

  useEffect(() => {
    fetchDevice()
  }, [remoteLockClient.isConnected])

  return (
    <>
      <TableHeader>
        <div css={roomTypeWidthStyle}>{t('Room type')}</div>
        <div css={floorNumberWidthStyle}>{t('Number of floors')}</div>
        <div css={roomNameWidthStyle}>{t('Room name')}</div>
        <div css={deviceNameWidthStyle}>{t('Device name')}</div>
        <div css={serialNumberWidthStyle}>{t('Serial number')}</div>
      </TableHeader>
      <TableBody>
        {!remoteLockClient.isConnected && <TableDisabledOverlay />}
        {assignForm.fields.map((room, i) => (
          <Controller
            key={room.id}
            name={`assign.${i}.remoteDeviceId`}
            control={control}
            render={({ field: { onChange, value } }) => (
              <TableRow css={css({ '> div': { fontWeight: 500 } })}>
                <div css={roomTypeWidthStyle}>
                  {assignForm.fields[i].typeNameJa === assignForm.fields[i - 1]?.typeNameJa ? '' : assignForm.fields[i].typeNameJa}
                </div>
                <div css={floorNumberWidthStyle}>{room.floorNameJa}</div>
                <div css={roomNameWidthStyle}>{room.roomNumber}</div>
                <div css={deviceNameWidthStyle}>
                  <Select
                    value={remoteLockDeviceList?.length ? value : ''}
                    onChange={selectVal => {
                      onChange(selectVal)
                      parentOnChange()
                    }}
                    customStyle={css({ paddingBottom: 0, width: 263 })}
                  >
                    <AntSelect.Option value="">-</AntSelect.Option>
                    {remoteLockDeviceList?.map(device => (
                      <AntSelect.Option key={device.id} value={device.id}>
                        {device.name}
                      </AntSelect.Option>
                    ))}
                  </Select>
                </div>
                <div css={serialNumberWidthStyle}>{(value && deviceIdAndSerial[value]) || '-'}</div>
              </TableRow>
            )}
          />
        ))}
      </TableBody>
    </>
  )
}

const roomTypeWidthStyle = css({ width: '20%', paddingLeft: 32 })
const floorNumberWidthStyle = css({ width: '10%' })
const roomNameWidthStyle = css({ width: '15%' })
const deviceNameWidthStyle = css({ width: '33%' })
const serialNumberWidthStyle = css({ width: '22%' })
